/**
 * AssignmentAccessPage
 * ====================
 * Direct assignment access via magic link (/a/{assignment_access_token})
 *
 * This page provides one-click access to a specific assigned program.
 * Coaches share this link with clients for immediate program access.
 */

import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  assignmentService,
  type AssignmentWithProgress,
} from '../../services'
import { ProgressBar } from '../../components/assignment'
import { LoadingScreen } from '../../components/common'
import {
  categorizeTasks,
  getCurrentPeriodLabel,
  getUnlockLabel,
  getScheduleLabel,
  type ScheduledTaskItem,
  type CategorizedTasks,
} from '../../utils/taskVisibility'
import type { ProgramOrganizationType } from '../../types/program-scheduling.types'

function getThumbnailUrl(video: ScheduledTaskItem['video'] | undefined): string | null {
  if (video?.thumbnail_url) return video.thumbnail_url
  if (video?.mux_playback_id) {
    return `https://image.mux.com/${video.mux_playback_id}/thumbnail.png?width=192&height=128&fit_mode=smartcrop`
  }
  return null
}

export function AssignmentAccessPage() {
  const { token } = useParams<{ token: string }>()
  const [assignment, setAssignment] = useState<AssignmentWithProgress | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorType, setErrorType] = useState<'invalid' | 'expired' | 'generic'>('generic')

  // Get organization type (default to day_based for legacy programs)
  const organizationType: ProgramOrganizationType =
    (assignment?.program as any)?.organization_type || 'day_based'
  const assignedAt = assignment?.assigned_at || assignment?.created_at || ''

  // Categorize tasks based on visibility - MUST be called unconditionally (Rules of Hooks)
  const categorizedTasks: CategorizedTasks = useMemo(() => {
    if (!assignment) {
      return { today: [], overdue: [], upcoming: [], completed: [] }
    }
    const items = assignment.program.items as ScheduledTaskItem[]
    return categorizeTasks(items, assignedAt, organizationType)
  }, [assignment, assignedAt, organizationType])

  // Calculate total days/weeks for period label
  const maxDay = useMemo(() => {
    if (!assignment) return 1
    return Math.max(
      ...assignment.program.items.map((item: any) => item.scheduled_day || 1)
    )
  }, [assignment])

  const maxWeek = useMemo(() => {
    if (!assignment) return 1
    return Math.max(
      ...assignment.program.items.map((item: any) => item.scheduled_week || 1)
    )
  }, [assignment])

  const periodLabel = useMemo(() => {
    if (!assignedAt) return ''
    return getCurrentPeriodLabel(
      assignedAt,
      organizationType,
      organizationType === 'day_based' ? maxDay : undefined,
      organizationType === 'week_based' ? maxWeek : undefined
    )
  }, [assignedAt, organizationType, maxDay, maxWeek])

  const completedCount = categorizedTasks.completed.length
  const totalCount = assignment?.program?.items?.length || 0
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // Available tasks = today + overdue
  const availableTasks = useMemo(() => {
    return [...categorizedTasks.today, ...categorizedTasks.overdue]
  }, [categorizedTasks.today, categorizedTasks.overdue])

  useEffect(() => {
    if (token) {
      loadAssignment()
    }
  }, [token])

  const loadAssignment = async () => {
    if (!token) return
    setIsLoading(true)
    setError(null)

    try {
      // Validate assignment access token
      const tokenData = await assignmentService.validateAssignmentToken(token)

      if (!tokenData) {
        setError('This link is invalid or has expired.')
        setErrorType('invalid')
        return
      }

      if (!tokenData.isValid) {
        // Check if it's expired vs cancelled
        if (tokenData.assignment.status === 'expired' ||
            (tokenData.assignment.expires_at && new Date(tokenData.assignment.expires_at) < new Date())) {
          setError('This program access has expired. Please contact your provider for a new link.')
          setErrorType('expired')
        } else {
          setError('This program is no longer available.')
          setErrorType('invalid')
        }
        return
      }

      // Get full assignment with progress
      const data = await assignmentService.getAssignmentWithProgress(
        tokenData.assignment.id,
        tokenData.client.id
      )

      if (!data) {
        setError('Unable to load program content.')
        setErrorType('generic')
        return
      }

      setAssignment(data)
    } catch (err) {
      console.error('Failed to load program:', err)
      setError('Failed to load program. Please try again.')
      setErrorType('generic')
    } finally {
      setIsLoading(false)
    }
  }

  // Early returns AFTER all hooks are called
  if (isLoading) {
    return <LoadingScreen message="Loading your program..." />
  }

  if (error || !assignment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            errorType === 'expired' ? 'bg-orange-100' : 'bg-red-100'
          }`}>
            {errorType === 'expired' ? (
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {errorType === 'expired' ? 'Access Expired' : 'Program Unavailable'}
          </h1>
          <p className="text-gray-600">{error || 'Unable to load this program.'}</p>
          <p className="text-sm text-gray-500 mt-4">
            Please contact your provider for assistance.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-4">
            <img
              src="/logo-icon.png"
              alt="Exercise Toolbox"
              className="w-8 h-8 object-contain"
            />
            <span className="font-semibold text-gray-900">
              Exercise Toolbox
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {assignment.program.title}
          </h1>
          {assignment.program.description && (
            <p className="text-gray-600 mt-1">{assignment.program.description}</p>
          )}
          <p className="text-sm text-gray-500 mt-2">
            Welcome, {assignment.client.full_name}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900">Your Progress</h2>
              <span className="text-sm text-blue-600 font-medium">{periodLabel}</span>
            </div>
            <span className="text-sm text-gray-600">
              {completedCount} of {totalCount} exercises complete
            </span>
          </div>
          <ProgressBar
            completed={completedCount}
            total={totalCount}
            size="lg"
            color={
              progressPercent === 100
                ? 'green'
                : progressPercent > 50
                ? 'blue'
                : 'yellow'
            }
          />
          {progressPercent === 100 && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">
                    Congratulations!
                  </h3>
                  <p className="text-sm text-green-700">
                    You've completed all {totalCount} exercises in this program. Great work!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Provider notes */}
        {assignment.notes && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
            <h3 className="font-medium text-blue-900 mb-1">
              Note from your provider
            </h3>
            <p className="text-blue-800 text-sm">{assignment.notes}</p>
          </div>
        )}

        {/* Today's Tasks / Available */}
        {availableTasks.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <h2 className="font-semibold text-green-800">
                  {organizationType === 'day_based' ? "Today's Tasks" : 'Available Now'}
                </h2>
                <span className="text-sm text-green-600">
                  ({availableTasks.length} {availableTasks.length === 1 ? 'exercise' : 'exercises'})
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {availableTasks.map((item, index) => (
                <ExerciseRow
                  key={item.id}
                  item={item}
                  index={index}
                  token={token!}
                  assignmentId={assignment.id}
                  status="available"
                  organizationType={organizationType}
                  assignedAt={assignedAt}
                />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming (Locked) */}
        {categorizedTasks.upcoming.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h2 className="font-semibold text-gray-700">Upcoming</h2>
                <span className="text-sm text-gray-500">
                  ({categorizedTasks.upcoming.length} {categorizedTasks.upcoming.length === 1 ? 'exercise' : 'exercises'})
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {categorizedTasks.upcoming.map((item, index) => (
                <LockedExerciseRow
                  key={item.id}
                  item={item}
                  index={index}
                  organizationType={organizationType}
                  assignedAt={assignedAt}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed */}
        {categorizedTasks.completed.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <h2 className="font-semibold text-gray-900">Completed</h2>
                <span className="text-sm text-gray-500">
                  ({categorizedTasks.completed.length} {categorizedTasks.completed.length === 1 ? 'exercise' : 'exercises'})
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {categorizedTasks.completed.map((item, index) => (
                <ExerciseRow
                  key={item.id}
                  item={item}
                  index={index}
                  token={token!}
                  assignmentId={assignment.id}
                  status="completed"
                  organizationType={organizationType}
                  assignedAt={assignedAt}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no tasks available */}
        {availableTasks.length === 0 && categorizedTasks.upcoming.length > 0 && categorizedTasks.completed.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-center">
            <svg className="w-12 h-12 text-blue-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="font-semibold text-blue-900 mb-1">No tasks available yet</h3>
            <p className="text-sm text-blue-700">
              Your first exercises will unlock soon. Check back tomorrow!
            </p>
          </div>
        )}

        {/* Program details */}
        <div className="mt-8 text-sm text-gray-500 flex gap-6">
          {assignment.program.difficulty_level && (
            <span>
              Difficulty:{' '}
              {assignment.program.difficulty_level.charAt(0).toUpperCase() +
                assignment.program.difficulty_level.slice(1)}
            </span>
          )}
          {assignment.program.duration_weeks && (
            <span>Duration: {assignment.program.duration_weeks} weeks</span>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-8">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center">
          <p className="text-sm text-gray-500">Exercise Toolbox</p>
        </div>
      </footer>
    </div>
  )
}

interface ExerciseRowProps {
  item: ScheduledTaskItem
  index: number
  token: string
  assignmentId: string
  status?: 'available' | 'completed'
  organizationType?: ProgramOrganizationType
  assignedAt?: string
}

function ExerciseRow({
  item,
  index: _index,
  token,
  assignmentId,
  status = 'available',
  organizationType = 'day_based',
  assignedAt,
}: ExerciseRowProps) {
  const isCompleted = status === 'completed'
  const completionCount = item.progress?.completion_count || 0
  const scheduleLabel = assignedAt
    ? getScheduleLabel(item, organizationType)
    : ''

  return (
    <Link
      to={`/a/${token}/video/${assignmentId}/${item.id}`}
      className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
    >
      {/* Exercise number/status */}
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isCompleted
            ? 'bg-green-100 text-green-600'
            : 'bg-blue-100 text-blue-600'
        }`}
      >
        {isCompleted ? (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
      </div>

      {/* Thumbnail */}
      <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
        {getThumbnailUrl(item.video) ? (
          <img
            src={getThumbnailUrl(item.video)!}
            alt={item.video?.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Exercise info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900 truncate">
            {item.video?.title || 'Exercise'}
          </h3>
          {scheduleLabel && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {scheduleLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
          {item.sets && <span>{item.sets} sets</span>}
          {item.reps && <span>{item.reps} reps</span>}
          {item.duration_seconds && (
            <span>{Math.floor(item.duration_seconds / 60)} min</span>
          )}
          {item.video?.duration_seconds && (
            <span>
              {Math.floor(item.video.duration_seconds / 60)}:
              {String(Math.floor(item.video.duration_seconds % 60)).padStart(2, '0')}
            </span>
          )}
        </div>
        {item.notes && (
          <p className="text-sm text-gray-600 mt-1 truncate">{item.notes}</p>
        )}
      </div>

      {/* Completion badge */}
      <div className="flex-shrink-0 text-right">
        {isCompleted && (
          <span className="text-xs text-green-600">
            {completionCount > 1 ? `${completionCount}x completed` : 'Completed'}
          </span>
        )}
      </div>

      {/* Arrow */}
      <svg
        className="w-5 h-5 text-gray-400 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </Link>
  )
}

interface LockedExerciseRowProps {
  item: ScheduledTaskItem
  index: number
  organizationType: ProgramOrganizationType
  assignedAt: string
}

function LockedExerciseRow({
  item,
  organizationType,
  assignedAt,
}: LockedExerciseRowProps) {
  const unlockLabel = getUnlockLabel(item, assignedAt, organizationType)
  const scheduleLabel = getScheduleLabel(item, organizationType)

  return (
    <div className="flex items-center gap-4 px-6 py-4 opacity-60">
      {/* Lock icon */}
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-400">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>

      {/* Thumbnail (grayed out) */}
      <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 grayscale">
        {getThumbnailUrl(item.video) ? (
          <img
            src={getThumbnailUrl(item.video)!}
            alt={item.video?.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Exercise info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-500 truncate">
            {item.video?.title || 'Exercise'}
          </h3>
          {scheduleLabel && (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
              {scheduleLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
          {item.sets && <span>{item.sets} sets</span>}
          {item.reps && <span>{item.reps} reps</span>}
          {item.video?.duration_seconds && (
            <span>
              {Math.floor(item.video.duration_seconds / 60)}:
              {String(Math.floor(item.video.duration_seconds % 60)).padStart(2, '0')}
            </span>
          )}
        </div>
      </div>

      {/* Unlock date */}
      <div className="flex-shrink-0">
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {unlockLabel}
        </span>
      </div>
    </div>
  )
}
